import * as React from 'react';
import { observable, action } from 'mobx';
import { observer } from 'mobx-react';
import { IAuftragsListeResponse, IAuftragStatus, IAuftrag } from '../../models/ApiTypes';
import { ApiStore } from '../../stores/ApiStore';
import { bind, inject } from '../../utils/decorators';
import Pager from '../site/Pager';
import FilterBox from '../site/FilterBox';
import PagedList from '../site/PagedList';
import { FormControl, InputGroup, Panel, ListGroup, ListGroupItem, Badge, Alert } from 'react-bootstrap';
import FaIcon from '../site/FaIcon';
import { UiStore } from '../../stores/UiStore';

interface IAuftragListItemProps {
  auftrag: IAuftrag;
  showLiDetails?: boolean;
}

/** Einzelnes Listenelement mit Auftragsinformationen */
class AuftragListItem extends React.Component<IAuftragListItemProps, {}> {

  @inject(UiStore) uiStore: UiStore;

  render() {
    const status = this.props.auftrag.auftragstatus.toLowerCase();
    return (
      <ListGroupItem header={this.props.auftrag.auftrag_name} onClick={() => this.uiStore.navigateTo(`/auftrag/${this.props.auftrag.anr}`)}>
        <Badge className={`auftrag-badge-${status}`} style={{float: 'right'}}>{this.props.auftrag.statustext}</Badge>
        <span className="text-muted">
          {this.props.showLiDetails ? `Zugewiesen an ${this.props.auftrag.pivorname} ${this.props.auftrag.piname}` : ''}
          {this.props.showLiDetails && <br/>}
          {this.props.auftrag.referenzdatum} - {this.props.auftrag.refnr}
        </span>
      </ListGroupItem>
    );
  }
}

interface IAuftraegeProps {
  lizenzAuftraege: boolean;
}

/** Liste aller Aufträge mit Filter und Suchfunktionen */
@observer
export default class Auftraege extends PagedList<IAuftraegeProps> {

  @inject(ApiStore) apiStore: ApiStore;
  @observable auftraege?: IAuftragsListeResponse;
  @observable auftragStatus: IAuftragStatus = { kuerzel: '', text: ''};
  @observable auftraegeRuecksprache: number = 0;

  // Die Liste beinhaltet nur die Status die im Portal Sinn machen und diese in leicht abgeänderter Reihenfolge
  private auftragStatusListe: Array<IAuftragStatus> = [
    { kuerzel: '', text: 'Alle Aufträge' },
    { kuerzel: 'inarbeit', text: 'in Arbeit' },
    { kuerzel: 'anfrage', text: 'Anfrage' },
    { kuerzel: 'angebot', text: 'Angebot versendet' },
    { kuerzel: 'auftert', text: 'Auftrag erteilt' },
    { kuerzel: 'termin', text: 'Terminiert' },
    { kuerzel: 'externab', text: 'Extern abgeschlossen' },
    { kuerzel: 'dokpruef', text: 'In Dokumentenprüfung' },
    { kuerzel: 'rueckspr', text: 'Rücksprache erforderlich' },
    { kuerzel: 'freigabe', text: 'Geprüft und freigegeben' },
    { kuerzel: 'abger', text: 'Abgerechnet' },
    { kuerzel: 'abgeschl', text: 'Abgeschlossen' },
    { kuerzel: 'auftnert', text: 'Auftrag nicht erteilt' },
    { kuerzel: 'sammelre', text: 'Sammelrechnung' },
    { kuerzel: 'zurueck', text: 'Zurückgestellt' }
  ];

  componentDidMount() {
    this.auftragStatus = this.auftragStatusListe[1];
    this.ladeAuftraege();
  }

  async ladeAuftraege() {
    // Aufträge für den gewählten Status und Filter laden
    {
      let res = await this.apiStore.auftraege(this.currentPage, this.filter, this.auftragStatus.kuerzel, this.props.lizenzAuftraege);
      if (res.ok) {
        this.setLastPage(res.json.last_page);
        this.setTotal(res.json.total);
        this.setAuftraege(res.json.data);
      }
    }
    // Abfragen ob der Nutzer Aufträge hat die auf Rücksprache warten, diese Information wird zur Anzeige eines Hinweises genutzt
    {
      let res = await this.apiStore.auftraegeanzahl('rueckspr');
      if (res.ok) {
        this.setAuftraegeRuecksprache(res.json.anzahl);
      } else {
        this.setAuftraegeRuecksprache(0);
      }
    }
  }

  @action setAuftraege(auftraege: IAuftragsListeResponse) {
    this.auftraege = auftraege;
  }

  @bind @action paginate(pageNumber: number) {
    this.setCurrentPage(pageNumber);
    this.ladeAuftraege();
  }

  @bind @action setAuftraegeRuecksprache(anzahl: number) {
    this.auftraegeRuecksprache = anzahl;
  }

  @bind @action setAuftragStatus(value: string) {
    let status = this.auftragStatusListe.find((e) => {return e.kuerzel === value; });
    if (status) {
      this.auftragStatus = status;
    } else {
      this.auftragStatus = { kuerzel: '', text: ''};
    }
    this.paginate(1);
  }

  @bind @action handleAuftragStatusChanged(event: React.FormEvent<FormControl>) {
    let status = this.auftragStatusListe.find((e) => {return e.kuerzel === (event.target as HTMLInputElement).value; });
    if (status) {
      this.auftragStatus = status;
    } else {
      this.auftragStatus = { kuerzel: '', text: ''};
    }
    this.paginate(1);
  }

  render() {
    let placeholder = 'Suchen (Name, Referenznr.)';
    if (this.props.lizenzAuftraege) {
      placeholder = 'Suchen (Name, Referenznr., Bearbeiter)';
    }
    return (
      <div className="auftraege">
        {!this.auftraege ? <div className="maxwidth">Lade Aufträge...</div> : (
          <div className="maxwidth">
            {this.auftraegeRuecksprache > 0 &&
              <Alert bsStyle="danger">
                Sie haben {this.auftraegeRuecksprache} {this.auftraegeRuecksprache === 1 ? 'Auftrag der auf Rücksprache wartet' : 'Aufträge die auf Rücksprache warten'}.
                Bitte <a href="#" onClick={() => this.setAuftragStatus('rueckspr')}>klicken Sie hier</a> um diese(n) anzuzeigen.
              </Alert>
            }
            <InputGroup className="filter-group">
              <InputGroup.Addon><FaIcon icon="folder-open" /></InputGroup.Addon>
              <FormControl componentClass="select" placeholder="select" className="filter-box" value={this.auftragStatus.kuerzel} onChange={this.handleAuftragStatusChanged}>
                {this.auftragStatusListe.map((e) => ( <option key={e.kuerzel} value={e.kuerzel}>{e.text}</option> ))};
              </FormControl>
            </InputGroup>
            <FilterBox onChange={this.handleFilterList} placeholder={placeholder} />
            <Panel header={`${this.auftragStatus.text} (${this.total})`}>
              <ListGroup fill={true} className="auftragsliste">
                {this.auftraege.length === 0 && (<ListGroupItem>keine Einträge</ListGroupItem>)}
                {this.auftraege.map((auftrag, index) => <AuftragListItem key={index} auftrag={auftrag} showLiDetails={this.props.lizenzAuftraege} />)}
              </ListGroup>
            </Panel>
            <Pager items={this.lastPage} onSelect={this.paginate} activePage={this.currentPage}/>
          </div>
        )}
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/auftraege/Auftraege.tsx